import { useAssetHub } from '@/managers'
import ctl from '@netlify/classnames-template-literals'
import { AnimatePresence, motion } from 'motion/react'
import { ChangeEvent, ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	FloatingWidget,
	Glyphs,
	StatefulImage,
	SubtleLoader,
	Tooltip,
	WidgetInputLargeTextField,
} from '../../rapid-ui'
import { logger } from '../../utilities'
import { MediaAsset } from '../../views'
import { useImageCache } from '../images'
import type { ImageAction } from '../tracking'

const CellVariants = {
	initial: {
		opacity: 0,
	},
	animate: {
		opacity: 1,
		scale: 1,
	},
	exit: {
		opacity: 0,
	},
}

export type ImageFilmstripProps = {
	assets: Array<MediaAsset>
	selectedAsset: string
	onSelect: (asset: MediaAsset) => void
	/** Omit to hide the upload button — its file picker would otherwise open and go nowhere. */
	onQuickUpload?: (file: File) => void
	/** Omit to hide the generate button, same reason. */
	onQuickGenerate?: (prompt: string, onComplete: () => void, onError?: (message: string) => void) => void
	isQuickUploading?: boolean
	onImageAction?: (action: ImageAction) => void
}

export function ImageFilmstrip(props: ImageFilmstripProps) {
	const { t } = useTranslation(['editor', 'common'])
	const assetHub = useAssetHub()
	const { getImage } = useImageCache()

	const handleSelect = useCallback((asset: MediaAsset) => {
		props.onImageAction?.('selected')
		props.onSelect(asset)
	}, [props.onSelect, props.onImageAction])

	const handleOpenAssetHub = useCallback(async () => {
		props.onImageAction?.('library_opened')
		try {
			const asset = await assetHub.open()
			if (asset) {
				handleSelect(asset)
			}
		} catch (error) {
			// The picker rejects with an AbortError when re-opened by another
			// caller — that's expected; anything else is a real failure worth
			// recording.
			if (error instanceof DOMException && error.name === 'AbortError') {
				return
			}
			logger.error(error, {
				tags: { component: 'ImageFilmstrip', action: 'open-asset-hub' },
			})
		}
	}, [assetHub, handleSelect, props.onImageAction])

	const sortedAssets = useMemo(() => {
		return [...props.assets]
			.filter((asset, i, a) => a.findIndex((a) => a.id === asset.id) === i)
			.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
	}, [props.assets])

	const inputRef = useRef<HTMLInputElement>(null)
	const handleQuickUpload = (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) {
			// An empty FileList is still truthy — a cancelled picker reaches here on some browsers.
			logger.warn('image-filmstrip: quick upload fired with no file', {
				tags: { component: 'ImageFilmstrip', action: 'quick-upload' },
			})
			return
		}
		props.onQuickUpload?.(file)
	}

	const openFilePicker = useCallback(() => {
		inputRef.current?.click()
	}, [])

	// ref to popup textarea
	const generatePromptRef = useRef<HTMLTextAreaElement>(null)
	const [generatePromptText, setGeneratePromptText] = useState('')
	const [generatePromptError, setGeneratePromptError] = useState<string | null>(null)
	const trimmedGeneratePromptText = generatePromptText.trim()
	const isGeneratePromptValid = trimmedGeneratePromptText.length >= 2

	const handleQuickGenerate = useCallback((prompt: string, onComplete: () => void) => {
		const trimmedPrompt = prompt.trim()
		if (trimmedPrompt.length < 2) {
			setGeneratePromptError(t('widgets.promptMinLengthError'))
			return false
		}
		setGeneratePromptError(null)
		props.onImageAction?.('generate_prompt_submitted')
		props.onQuickGenerate?.(trimmedPrompt, onComplete, (message) => {
			setGeneratePromptError(message)
		})
		return true
	}, [props.onQuickGenerate, props.onImageAction, t])

	return (
		<div className="grid grid-cols-[repeat(auto-fill,minmax(64px,1fr))] gap-2 p-2">
			{props.onQuickUpload && (
				<input ref={inputRef} type="file" accept="image/*" hidden onChange={handleQuickUpload} />
			)}

			<AnimatePresence>
				{sortedAssets.map((asset) => (
					<motion.div
						layout
						variants={CellVariants}
						initial="initial"
						animate="animate"
						exit="exit"
						key={asset.id}
						className={ctl(`
							aspect-square rounded-md overflow-hidden flex items-center justify-center group
							relative
							${
							props.selectedAsset === asset.id
								? 'ring-3 ring-brand-500'
								: 'active:ring-3 active:ring-brand-500'
						}
							${!asset.isReady && 'animate-pulse cursor-not-allowed'}
						`)}
					>
						<div className="absolute inset-0 rounded-md">
							<StatefulImage
								src={asset.url}
								alt=""
								className="h-full w-full"
								objectFit="cover"
								objectPosition="center"
								isGenerating={!asset.isReady}
								generatingPlaceholder={<SubtleLoader width={20} />}
							/>
						</div>
						{props.selectedAsset === asset.id && (
							<div className="absolute top-1 right-1 text-purple-600">
								<Glyphs.micro.BoldCheckIcon fillCheckmark={true} />
							</div>
						)}
						{asset.isReady && (
							<div
								className="cursor-pointer absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center "
								onClick={() => handleSelect(asset)}
							>
								<div className="text-xs font-bold text-white px-1 py-0.5 rounded inline-block bg-black/80">
									{t('widgets.apply')}
								</div>
							</div>
						)}
					</motion.div>
				))}

				{
					/*
					Loading placeholder when an asset is selected but not in the history yet,
					OR if a quick upload is in progress.
				*/
				}
				{(props.isQuickUploading
					|| (sortedAssets.find(asset => asset.id === props.selectedAsset) === undefined
						&& props.selectedAsset
						&& !getImage(props.selectedAsset).isEmpty))
					&& (
						<motion.div
							layout
							variants={CellVariants}
							initial="initial"
							animate="animate"
							exit="exit"
							key="loading-item"
							className={ctl(`
							aspect-square bg-gradient-to-br from-grey-100 to-grey-200 rounded-md flex items-center justify-center group
							ring-3 ring-brand-500 animate-pulse cursor-not-allowed
						`)}
						>
							<SubtleLoader width={20} />
						</motion.div>
					)}
			</AnimatePresence>
			<>
				<FilmstripButton
					onClick={handleOpenAssetHub}
					label={t('settingsNav.assetHub', { ns: 'common' })}
				>
					<Glyphs.ultimate.GalleryIcon />
				</FilmstripButton>

				{props.onQuickUpload && (
					<FilmstripButton
						onClick={() => {
							props.onImageAction?.('quick_upload_clicked')
							openFilePicker()
						}}
						label={t('widgets.upload')}
						tooltip={
							<div className="text-center">
								<div>{t('widgets.uploadOwnImages')}</div>
								<div className="text-[10px] text-grey-500 mt-0.5">
									{t('widgets.supportedFormatsHint')}
								</div>
							</div>
						}
					>
						<Glyphs.ultimate.UploadIcon />
					</FilmstripButton>
				)}

				{props.onQuickGenerate && (
					<FloatingWidget
						label={t('widgets.generate')}
						className="grid"
						initialFocus={generatePromptRef}
						widget={({ close }) => (
							<form
								onSubmit={(e) => {
									e.preventDefault()
									handleQuickGenerate(generatePromptText, () => {
										setGeneratePromptText('')
										close()
									})
								}}
								className="grid gap-2 mt-1"
							>
								<WidgetInputLargeTextField
									value={generatePromptText}
									onChange={(nextValue) => {
										setGeneratePromptText(nextValue)
										if (generatePromptError) { setGeneratePromptError(null) }
									}}
									label={t('widgets.describeImageToGenerate')}
									inputRef={generatePromptRef}
									onCmdEnter={() => {
										handleQuickGenerate(generatePromptText, () => {
											setGeneratePromptText('')
											close()
										})
									}}
								/>
								{generatePromptError && (
									<div className="text-xs text-red-600">{generatePromptError}</div>
								)}
								<Button
									submit={true}
									type="colorful"
									fullWidth={true}
									size="short"
									disabled={!isGeneratePromptValid}
								>
									{t('widgets.generate')}
								</Button>
							</form>
						)}
					>
						{({ active }) => (
							<FilmstripButton
								onClick={() => {
									if (!active) { props.onImageAction?.('quick_generate_clicked') }
								}}
								label={t('widgets.generate')}
							>
								<Glyphs.ultimate.ImageGenerateIcon />
							</FilmstripButton>
						)}
					</FloatingWidget>
				)}
			</>
		</div>
	)
}

type FilmstripButtonProps = {
	children: ReactNode
	onClick?: () => void
	label: string
	tooltip?: ReactNode
}

function FilmstripButton({ children, onClick = () => {}, label, tooltip }: FilmstripButtonProps) {
	const button = (
		<motion.button
			layout
			variants={CellVariants}
			initial="initial"
			animate="animate"
			exit="exit"
			key="add-item"
			type="button"
			className="aspect-square rounded-md bg-gradient-to-br from-grey-50 to-grey-100 flex flex-col gap-1 items-center justify-center cursor-pointer w-full h-full"
			onClick={onClick}
		>
			<div className="size-6 mt-1">
				{children}
			</div>
			{label && <div className="text-[10px] font-medium text-grey-700">{label}</div>}
		</motion.button>
	)

	if (tooltip) {
		return (
			<Tooltip content={tooltip} wrapperClassName="w-full h-full">
				{button}
			</Tooltip>
		)
	}

	return <>{button}</>
}
